/**
 * Core-100 R4 (CORE-AW): a shop's partial-day absences for a date range, grouped by technician.
 *
 * `status` is explicit: when the read fails the capacity built from it is PROVISIONAL (it may overstate
 * availability), and surfaces say so instead of presenting it as known.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  ABSENCE_INTERVAL_CAPACITY_COLUMNS,
  groupAbsenceIntervals,
  type AbsenceIntervalRow,
  type AbsenceIntervalsByTech,
} from '@/utils/capacity/absenceIntervals';

export type AbsenceIntervalsStatus = 'loading' | 'known' | 'error';

const EMPTY: AbsenceIntervalsByTech = new Map();

export function useTechnicianAbsenceIntervals(
  shopId: string | null | undefined,
  fromDate: string | null | undefined,
  toDate: string | null | undefined,
): { byTech: AbsenceIntervalsByTech; status: AbsenceIntervalsStatus } {
  const enabled = !!shopId && !!fromDate && !!toDate;
  const q = useQuery({
    queryKey: ['technician-absence-intervals', shopId, fromDate, toDate],
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technician_absence_intervals')
        .select(ABSENCE_INTERVAL_CAPACITY_COLUMNS)
        .eq('shop_id', shopId as string)
        .gte('absence_date', fromDate as string)
        .lte('absence_date', toDate as string);
      if (error) throw error;
      return groupAbsenceIntervals((data || []) as unknown as AbsenceIntervalRow[]);
    },
  });
  if (!enabled || q.isLoading) return { byTech: EMPTY, status: 'loading' };
  if (q.isError || !q.data) return { byTech: EMPTY, status: 'error' };
  return { byTech: q.data, status: 'known' };
}
